import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { ForecastRequest } from '../shared/dto/forecast/forecast-request';
import { RequestService } from '../shared/services/request-service';
import { ForecastResponse } from '../shared/dto/forecast/forecast-response';
import { ForecastOverview } from '../shared/dto/forecast/forecast-overview';
import { ForecastUser } from '../shared/dto/forecast/forecast-user';
import { PatentCountryAbandonDate } from './report-forecast/report-forecast-summary-details/report-forecast-summary-details.component';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '../security/auth.service';
import { ForecastDownloadDetails, ForecastDownloadRequest } from './report-forecast/report-download-modal/report-download-modal.model';
import { ForecastStatusAction, ForecastUserStats } from '../shared/dto/forecast/forecast-user-stats';
import { GeneralResponseMessage } from '../shared/dto/general-response-message';
import { DocumentService } from '../data-services/document.service';
import { map, timeout } from 'rxjs/operators';
import { FeeAdjustmentRequest } from '../shared/request/fee-adjustment-request';
import { PaginatedData, PaginateObject } from '../shared/dto/paginated-data';
import { ForecastPaginated } from '../shared/dto/forecast/forecast-paginated';
import { RequestFilterNumberResponse } from '../shared/query/request-filter.component';
import { FieldType } from '../shared/data-filter/field-type';
import { OPERATORS } from '../shared/data-filter/filter-operators';
import { RsqlEncoder } from '../shared/data-filter/rsql-encoder';
import { cloneDeep } from '../shared/clone-deep';
import { ColumnMatchData } from '../shared/dto/column-match-data';
import { ForecastImportDocumentsRequest } from '../shared/request/forecast-import-documents-request';
import { ForecastImportDocumentsResponse } from '../shared/response/forecast-import-documents-response';

@Injectable()
export class ForecastService {

  public patentAbandonDateChanged: Subject<PatentCountryAbandonDate> = new Subject<PatentCountryAbandonDate>();
  public forecastDataChanged: Subject<ForecastResponse> = new Subject<ForecastResponse>();

  public forecastDownloadTriggered: Subject<ForecastDownloadDetails> = new Subject<ForecastDownloadDetails>();
  public forecastUsers: ForecastUser[];

  constructor(private httpClient: HttpClient,
              private authService: AuthService,
              private requestService: RequestService,
              private docService: DocumentService) {
    this.authService.connectedEmitter.subscribe(connected => {
      if (!connected) {
        this.clear();
      }
    });
  }

  public downloadForecast(downloadRequirements?: ForecastDownloadDetails): void {
    this.forecastDownloadTriggered.next(downloadRequirements);
  }

  public clear(): void {
    this.patentAbandonDateChanged = new Subject<PatentCountryAbandonDate>();
  }

  public getForecastReportById(forecastId: number): Promise<ForecastResponse> {
    const url = `/api/forecast/${forecastId}`;
    const headers = this.requestService.buildHttpHeaders();
    // TODO: Response type dto for getForecastReportById
    const forecastPromise = this.httpClient.get<ForecastResponse>(url, { headers: headers })
      .toPromise();

    return forecastPromise;
  }

  public createForecast(data: ForecastRequest): Promise<ForecastResponse> {
    const timeoutDuration = 60000; // 60 seconds if possible
    const url = '/api/forecast';
    const headers = this.requestService.buildHttpHeaders();

    return this.httpClient
      .post<ForecastResponse>(url, data, { headers: headers })
      .pipe(
        timeout(timeoutDuration)
      )
      .toPromise();
  }

  public parseImportHeader(fileKey: string): Observable<ColumnMatchData[]> {
    const url = '/api/forecast/import/parse-header';
    const headers = this.requestService.buildHttpHeaders();

    return this.httpClient.post<ColumnMatchData[]>(url, { fileKey: fileKey }, { headers: headers });
  }

  public importDocuments(request: ForecastImportDocumentsRequest): Observable<ForecastImportDocumentsResponse> {
    const url = '/api/forecast/import/documents';
    const headers = this.requestService.buildHttpHeaders();

    return this.httpClient.post<ForecastImportDocumentsResponse>(url, request, { headers: headers });
  }

  public adjustFees(feeAdjustmentRequest: FeeAdjustmentRequest): Observable<FeeAdjustmentRequest> {
    const url = '/api/forecast/fee-adjustment';
    const headers = this.requestService.buildHttpHeaders();
    return this.httpClient.post<FeeAdjustmentRequest>(url, feeAdjustmentRequest, { headers: headers });
  }

  public getAllPaginated(request: PaginatedData<ForecastPaginated>): Observable<PaginatedData<ForecastPaginated>> {
    const url = '/api/forecast/paginated';
    const headers = this.requestService.buildHttpHeaders();

    return this.httpClient
      .post<any>(url, request.toObject(), { headers: headers })
      .pipe(
        // Update the given pagination object with values from the response.
        map((response: PaginateObject<ForecastPaginated>) => request.update(response))
      );
  }

  public getSum(request: PaginatedData<ForecastPaginated>): Observable<number> {
    const url = '/api/forecast/sum';
    const headers = this.requestService.buildHttpHeaders();

    return this.httpClient
      .post<RequestFilterNumberResponse>(url, request.toObject(), { headers: headers })
      .pipe(
        // Update the given pagination object with values from the response.
        map((response: RequestFilterNumberResponse) => response.count),
      );
  }

  public getCount(request: PaginatedData<ForecastPaginated>, dateRange: number, query: string = ''): Observable<number> {
    const url = '/api/forecast/counts';
    const headers = this.requestService.buildHttpHeaders();

    const rsqlEncoder = new RsqlEncoder();
    const dateQuery = rsqlEncoder.encode({
      field: 'creationDate',
      type: FieldType.Numeric,
      value: dateRange,
      operator: OPERATORS.common.$gte,
    });

    const requestPaginated = cloneDeep(request);
    requestPaginated.query = RsqlEncoder.combine([dateQuery, query]);

    return this.httpClient
      .post<RequestFilterNumberResponse>(url, requestPaginated.toObject(), { headers: headers })
      .pipe(
        // Update the given pagination object with values from the response.
        map((response: RequestFilterNumberResponse) => response.count),
      );
  }

  public getForecastByCompanyId(companyId: number): Promise<ForecastOverview[]> {
    const url = `/api/forecasts/company/${companyId}`;
    const headers = this.requestService.buildHttpHeaders();
    return this.httpClient.get<ForecastOverview[]>(url, { headers: headers })
      .toPromise();
  }

  public getUserForecastStats(companyUserId: number,
    startDate?: number,
    endDate?: number): Promise<ForecastUserStats[]> {
    const url = `/api/forecast/stats/${companyUserId}`;
    const headers = this.requestService.buildHttpHeaders();

    if (startDate && endDate) {
      return this.httpClient.get<ForecastUserStats[]>(url, {
        headers: headers,
        params: { start: startDate.toString(), end: endDate.toString() }
      })
        .toPromise();
    }

    return this.httpClient.get<ForecastUserStats[]>(url, { headers: headers })
      .toPromise();
  }

  public updateUserForecastStats(companyUserId: number,
    forecastId: number,
    statsAction: ForecastStatusAction): Promise<any> {
    const url = `/api/forecast/stats/${companyUserId}`;
    const headers = this.requestService.buildHttpHeaders();

    return this.httpClient.post(url,
      { forecastOverviewId: forecastId, statsAction: statsAction }, { headers: headers })
      .toPromise();
  }

  public getForecastUsers(): Promise<ForecastUser[]> {
    const url = '/api/forecast/users';
    const headers = this.requestService.buildHttpHeaders();
    return this.httpClient.get<ForecastUser[]>(url, { headers: headers })

      .toPromise();
  }

  public getForecastUser(customerId: number): ForecastUser {
    if (this.forecastUsers) {
      return this.forecastUsers.find(user => user.customer.id === customerId);
    }
    return null;
  }

  public downloadForecastExcel(downloadRequest: ForecastDownloadRequest): void {
    const url = '/api/forecast/generate/excel';
    const headers = this.requestService.buildHttpHeaders();

    this.httpClient.post(url, downloadRequest, {
      headers: headers,
      observe: 'response',
      responseType: 'blob'
    })
      .toPromise()
      .then(resp => {
        this.docService.saveFile(resp);
      }
      );
  }

  public sendDownloadedForecastPdf(downloadRequest: ForecastDownloadRequest): Promise<GeneralResponseMessage> {

    const url = '/api/forecast/email/file';
    const headers = this.requestService.buildHttpHeaders();
    return this.httpClient.post<GeneralResponseMessage>(url, downloadRequest, { headers: headers })
      .toPromise();
  }

  public linkForecastEstimate(forecastId: number, estimateId: number): Promise<any> {
    const url = `/api/forecast/${forecastId}/link/estimate/${estimateId}`;
    const headers = this.requestService.buildHttpHeaders();
    return this.httpClient.put<any>(url, { headers: headers })
      .toPromise();
  }

  public unlinkForecastEstimate(forecastId: number, estimateId: number): Promise<any> {
    const url = `/api/forecast/${forecastId}/unlink/estimate/${estimateId}`;
    const headers = this.requestService.buildHttpHeaders();
    return this.httpClient.put<any>(url, { headers: headers })
      .toPromise();
  }

  /**
   * Updates the forecast without doing a re-run.
   * If you need to update patents or documents, use createForecast
   *
   * @param data
   */
  public updateForecast(data: ForecastRequest): Promise<any> {
    const url = `/api/forecast/${data.forecastOverviewId}`;
    const headers = this.requestService.buildHttpHeaders();
    return this.httpClient.put<any>(url, data, { headers: headers })
      .toPromise();
  }

}
